<template>
  <v-card :disabled="loading" :loading="loading" flat>
    <v-text-field
      :hint="t('donationDetails.form.recipientName.hint')"
      :label="t('donationDetails.form.recipientName.label')"
      :placeholder="t('donationDetails.form.recipientName.placeholder')"
      :rules="[rules.requiredField(t('donationDetails.form.recipientName.label'))]"
      :value="getAttributeValueById(attributes.subjectName)"
      data-cy="donation-details-form-recipientname-field"
      required
      @change="setAttributeValueById(attributes.subjectName, $event, false)"
    />
    <donation-cause-autocomplete-component
      v-if="showCause && !hasAnyActiveRequestSessionOrganizationGroupCauses"
      v-model="donationDetails.donationCause"
      :clearable="false"
      :hide-details="false"
      :placeholder="t('donationDetails.form.donationCauseId.placeholder')"
      :rules="[rules.requiredField(t('donationDetails.form.donationCauseId.autoComplete.fund'))]"
      :default-donation-causes="defaultSelectableDonationCauses as DonationCause[]"
      data-cy="donation-details-form-autocomplete"
      @change="fetchImages(donationDetails.donationCause?.organizationGroup.id)"
    />
    <v-autocomplete
      v-else-if="showCause"
      v-model="donationDetails.donationCause"
      :allow-overflow="false"
      :hint="t('donationDetails.form.donationCauseId.hint')"
      :items="activeRequestSessionOrganizationGroupCauses"
      :label="t('donationDetails.form.donationCauseId.label')"
      :menu-props="{ closeOnContentClick: true }"
      :placeholder="t('donationDetails.form.donationCauseId.placeholder')"
      :rules="[rules.requiredField(t('donationDetails.form.donationCauseId.label'))]"
      data-cy="donation-details-form-autocomplete"
      item-text="name"
      required
      return-object
      @change="fetchImages(donationDetails.donationCause?.organizationGroup.id)"
    >
      <template #item="{ item }">
        <v-list-item-media v-if="item.value.logoUrl && item.value.logoUrl.length > 0">
          <v-img
            :src="item.value.logoUrl"
            :alt="
              t('donationDetails.form.donationCauseId.autoComplete.logoAltText', {
                causeName: item.value.name,
              })
            "
          />
        </v-list-item-media>
        <div class="v-list-item__content">
          <v-list-item-title>
            {{ item.value.name }}
          </v-list-item-title>
          <v-list-item-subtitle>
            <span
              v-if="
                item.value.organizationGroup &&
                item.value.organizationGroup.name !== item.value.name
              "
            >
              {{
                t('donationDetails.form.donationCauseId.autoComplete.fundName', {
                  fundName: item.value.organizationGroup.name,
                })
              }}
              |
            </span>
            <span
              v-if="item.value.organizationGroup && item.value.organizationGroup.giro"
              class="font-weight-medium"
              >{{ item.value.organizationGroup.giro }}</span
            >
          </v-list-item-subtitle>
        </div>
      </template>
    </v-autocomplete>
    <v-textarea
      v-model="greetingValue"
      :label="t('donationDetails.form.message.label')"
      :placeholder="t('donationDetails.form.message.placeholder')"
      counter="1000"
      auto-grow
      data-cy="donation-details-form-message-textarea"
      rows="2"
    >
      <template #append>
        <v-dialog
          v-if="exampleMessages"
          v-model="dialog"
          data-cy="donation-details-form-dialog"
          max-width="600px"
          transition="slide-y-transition"
        >
          <template #activator="{ props }">
            <v-btn color="primary" data-cy="donation-details-form-view-list" icon v-bind="props">
              <v-icon color="primary"> mdi-view-list </v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-card-title class="headline" primary-title>
              {{ t('donationDetails.exampleMessages.title') }}
            </v-card-title>
            <v-card-text>
              <p>{{ t('donationDetails.exampleMessages.text') }}</p>
              <v-divider />
              <v-list>
                <v-list-item
                  v-for="(item, index) in exampleMessages"
                  :key="index"
                  @click="selectMessage(item)"
                >
                  <v-list-item-title>{{ item }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-btn data-cy="donation-details-show-dialog-btn" text @click="dialog = !dialog">
                {{ t('generic.labels.close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </v-textarea>
    <!-- Show condolence on memorial page: Only displayed if the request session asks for it -->
    <v-slide-x-transition mode="out-in">
      <v-radio-group
        v-if="showCondolenceOptions"
        v-model="donationDetails.showCondolenceOnMemorialPage"
        row
        :rules="[rules.tristateBooleanRequired]"
      >
        <template #label>
          <div>{{ t('donationDetails.form.showCondolenceOnMemorialPage.label') }}</div>
        </template>
        <v-radio
          :label="t('generic.labels.yes')"
          :value="true"
          data-cy="donation-details-form-show-condolence-on-memorial-page-yes-radio"
        />
        <v-radio
          :label="t('generic.labels.no')"
          :value="false"
          data-cy="donation-details-form-show-condolence-on-memorial-page-no-radio"
        />
      </v-radio-group>
    </v-slide-x-transition>
    <v-textarea
      :hint="t('donationDetails.form.senderName.hint')"
      :label="t('donationDetails.form.senderName.label')"
      :placeholder="t('donationDetails.form.senderName.placeholder')"
      :rules="[validateSenderName]"
      :validate-on-blur="true"
      :value="getAttributeValueById(attributes.donorName)"
      auto-grow
      data-cy="donation-details-form-sendername-textarea"
      rows="1"
      @change="setAttributeValueById(attributes.donorName, $event, false)"
    />
    <v-checkbox
      v-model="noSenderName"
      :disabled="!!senderName"
      :false-value="false"
      :label="t('donationDetails.form.noSenderName.label')"
      :true-value="true"
      class="no-sender-name"
      color="primary"
      data-cy="donation-details-form-no-sender-name-check"
    />
    <component
      :is="isIphone ? 'ImagePickerIOSComponent' : 'ImagePickerComponent'"
      v-if="selectedImageId && selectedImageId > 0"
      v-model="selectedImageId"
      :categories="nonEmptyCategories"
      :hint="t('donationDetails.form.image.hint')"
      :images="images"
      :label="t('donationDetails.form.image.label')"
      :show-lazy="false"
      data-cy="donation-details-form-image-picker-component"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, nextTick } from 'vue';
import store from '@/store';
import { get } from '@/api/mainApi';
import { useDonationReset } from '@/composables/useDonationReset';
import { useDonationCauses } from '@/composables/useDonationCauses';
import ImagePickerComponent from '@/components/image/ImagePickerComponent.vue';
import ImagePickerIOSComponent from '@/components/image/ImagePickerIOSComponent.vue';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';

import { Donation } from '@/interfaces/donation/donation';
import { DonationCause } from '@/interfaces/organizationgroup/donationCause';
import DonationCauseAutocompleteComponent from '@/components/organizationgroup/donationcause/DonationCauseAutocompleteComponent.vue';
import { AttributeType } from '@/enums/attributeType';
import { DonationImage } from '@/interfaces/donation/donationImage';
import { DonationImageCategory } from '@/interfaces/donation/donationImageCategory';
import { DonationStep } from '@/interfaces/donationStep';
import DonationCauseSelectComponent from '@/components/organizationgroup/donationcause/DonationCauseSelectComponent.vue';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { useValidationRules } from '@/composables/useValidationRules';
import { useLabel } from '@/composables/useLabel';
import matomo from '@/services/matomoService';

export default defineComponent({
  components: {
    DonationCauseSelectComponent,
    ImagePickerComponent,
    ImagePickerIOSComponent,
    DonationCauseAutocompleteComponent,
  },
  props: {
    value: {
      type: Object as () => Donation,
      default: () => ({}) as Donation,
    },
    form: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:value'],
  setup(props, { emit }) {
    const loading = ref(false);
    const dialog = ref(false);
    const showCause = ref(false);
    const images = ref([] as DonationImage[]);
    const defaultImage = ref(null as DonationImage | null);
    const selectedImageId = ref(0);
    const categories = ref([] as DonationImageCategory[]);
    const noSenderName = ref(false);
    const attributes = ref({
      subjectName: AttributeType.SUBJECT_NAME,
      greeting: AttributeType.GREETING,
      donorName: AttributeType.DONOR_NAME,
    });
    const defaultSelectableDonationCauses = ref([] as DonationCause[]);
    const showCondolenceOptions = ref(false);
    const defaultDonationGuideVersion = ref(DonationGuideVersion.V1);
    const exampleMessages = ref([] as string[]);

    const { t } = useLabel();
    const { rules } = useValidationRules(t);

    // From StepperFlowMixin
    const getDonationStepFlowVersion = (): DonationGuideVersion => {
      return store.state.donationGuideVersion || defaultDonationGuideVersion.value;
    };

    // setStep removed (unused) — step updates happen via other flow components

    const { resetDonation } = useDonationReset();
    const { getDonationCauseWithId, getDonationCausesWithIds } = useDonationCauses();

    const reset = async () => {
      await resetDonation(donation.value);

      // Assuming availableSteps[1]
      nextTick(() => {});
    };

    // Computed
    const donationDetails = computed({
      get() {
        return props.value;
      },
      set(val) {
        emit('update:value', val);
      },
    });
    const isIphone = computed((): boolean => {
      const ua = navigator.userAgent || '';
      const isIpad = /\biPad\b/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);

      return /\biPhone\b/.test(ua) && !isIpad;
    });
    const greetingValue = computed({
      get(): string {
        return getAttributeValueById(attributes.value.greeting) || '';
      },
      set(value: string): void {
        setAttributeValueById(attributes.value.greeting, value, false);
      },
    });
    const requestSession = computed(() => store.state.requestSession!);
    const donation = computed(() => store.state.donation!);
    const currentStep = computed(() => store.state.currentStep);
    const senderName = computed(() => donationDetails.value.attributeValues[2].value);
    const nonEmptyCategories = computed(() => {
      if (images.value) {
        return categories.value.filter(
          (c) =>
            images.value.filter((i: DonationImage) => i.donationImageCategory.id === c.id).length
        );
      }
      return categories.value;
    });
    const hasDefaultDonationCauses = computed((): boolean => {
      return (
        !!donation.value.donationCase &&
        !!donation.value.donationCase.defaultDonationCauseIds &&
        donation.value.donationCase.defaultDonationCauseIds.length > 0
      );
    });
    const activeRequestSessionOrganizationGroupCauses = computed((): DonationCause[] => {
      if (requestSession.value.organizationGroup) {
        return requestSession.value.organizationGroup.donationCauses.filter(
          (donationCause: DonationCause) => {
            return donationCause.active;
          }
        );
      }
      return [];
    });
    const hasAnyActiveRequestSessionOrganizationGroupCauses = computed((): boolean => {
      return (
        !!activeRequestSessionOrganizationGroupCauses.value &&
        activeRequestSessionOrganizationGroupCauses.value.length > 0
      );
    });

    // Methods
    const calculateShowCause = () => {
      if (
        activeRequestSessionOrganizationGroupCauses.value &&
        activeRequestSessionOrganizationGroupCauses.value.length === 1
      ) {
        showCause.value = false;
        donationDetails.value.donationCause = activeRequestSessionOrganizationGroupCauses.value[0];
      } else {
        showCause.value = true;
      }
    };
    const getAttributeValueById = (id: number): string => {
      const attributeValue = donationDetails.value.attributeValues.find(
        (a) => a.attributeId === id
      );
      return attributeValue ? attributeValue.value : '';
    };
    const setAttributeValueById = (id: number, value, concat: boolean) => {
      const attributeValue = donationDetails.value.attributeValues.find(
        (a) => a.attributeId === id
      );

      if (attributeValue) {
        if (concat) {
          attributeValue.value += value;
        } else {
          attributeValue.value = value;
        }
      }
    };
    const fetchImages = (organizationGroupId) => {
      if (!organizationGroupId) {
        store.commit('setSnackbar', {
          active: true,
          color: 'error',
          text: 'No organization group id provided',
        });
        return;
      }
      Promise.all([
        get(ENDPOINTS.IMAGE_CATEGORIES, { organizationGroupId: organizationGroupId }),
        get(ENDPOINTS.IMAGES, { organizationGroupId: organizationGroupId }),
      ])
        .then(([categoriesRes, imagesRes]) => {
          if (categoriesRes) {
            categories.value = categoriesRes as DonationImageCategory[];
            categories.value.sort((a: DonationImageCategory, b: DonationImageCategory) => {
              const aComp = a.organizationGroupId == null ? 0 : 1;
              const bComp = b.organizationGroupId == null ? 0 : 1;
              return bComp - aComp;
            });
          }
          if (imagesRes) {
            images.value = (imagesRes as DonationImage[]).map((image: DonationImage) => ({
              ...image,
              url: BASE_URL + image.url + '?compression=true',
            }));
            // Retrieve the default image
            defaultImage.value = images.value.find((i) => i.defaultImage) as
              | DonationImage
              | undefined;
            if (defaultImage.value) {
              selectedImageId.value = defaultImage.value.id;
            } else if (images.value.length > 0) {
              // if the default image wasn't found, take the first best image (this should never happen)
              selectedImageId.value = images.value[0].id;
            } else {
              // this is disaster mode, and should absolutely not happen. We just pick the very first id, so that an image will be preselected
              selectedImageId.value = 1;
            }
          }
        })
        .catch(() => {
          store.commit('setSnackbar', {
            active: true,
            color: 'error',
            text: 'Failed to fetch images',
          });
        })
        .finally(() => {
          if (images.value) {
            images.value.sort();
          }
        });
    };
    const selectMessage = (message) => {
      setAttributeValueById(AttributeType.GREETING, message, true);
      dialog.value = false;
    };
    const validateSenderName = (value) => {
      if (!value && !noSenderName.value) {
        return t('donationDetails.form.noSenderName.error');
      }

      return true;
    };
    const handleDonationCausesFromDonationCase = async () => {
      loading.value = true;
      // handle defaultDonationCause from the selected DonationCase if one is selected
      // Only apply defaults for external agents, not for charities (who have organization group causes)
      if (
        hasDefaultDonationCauses.value &&
        !hasAnyActiveRequestSessionOrganizationGroupCauses.value
      ) {
        const causeIds: number[] = donation.value.donationCase?.defaultDonationCauseIds || [];
        if (causeIds.length == 1) {
          donationDetails.value.donationCause = await getDonationCauseWithId(causeIds[0]);
          fetchImages(donationDetails.value.donationCause?.organizationGroup.id);
        } else if (causeIds.length > 1) {
          defaultSelectableDonationCauses.value = await getDonationCausesWithIds(causeIds);
        }
      } else if (hasAnyActiveRequestSessionOrganizationGroupCauses.value) {
        // For charities with organization causes, show the organization's causes in the dropdown
        defaultSelectableDonationCauses.value = activeRequestSessionOrganizationGroupCauses.value;
      }
      loading.value = false;
    };
    // getDonationCauseWithId and getDonationCausesWithIds are now provided by useDonationCauses composable

    // Watch
    watch(currentStep, (newVal, oldVal) => {
      const newStepNumber = typeof newVal === 'number' ? newVal : (newVal as DonationStep)?.step;
      const oldStepNumber = typeof oldVal === 'number' ? oldVal : (oldVal as DonationStep)?.step;

      const targetStep =
        getDonationStepFlowVersion() === DonationGuideVersion.V1
          ? stepV1.DONATION_DETAILS
          : stepV2.CERTIFICATE_DETAILS;

      if (newStepNumber === targetStep) {
        setAttributeValueById(
          AttributeType.SUBJECT_NAME,
          donation.value.donationCase?.caseInfo?.name || '',
          false
        );
        calculateShowCause();

        if (oldStepNumber !== undefined && oldStepNumber < targetStep) {
          // defaultDonationCause is only set if going forward not when coming from a stepV1 after this one
          // we do this in case DonationCase has been changed
          handleDonationCausesFromDonationCase();
          matomo.trackEvent('donation_details', 'viewed', 'donations_details_viewed');
        }
      }
    });
    watch(
      greetingValue,
      (newValue) => {
        showCondolenceOptions.value =
          !!donation.value.showCondolenceOnMemorialPage && !!newValue.trim().length;
      },
      { immediate: true }
    );
    watch(senderName, (val) => {
      if (val) {
        noSenderName.value = false;
      }
    });
    watch(noSenderName, (val) => {
      if (val && !senderName.value) {
        // Assuming form.validate exists
        if (props.form) props.form.validate();
      }
    });
    watch(selectedImageId, (value) => {
      if (value) {
        donationDetails.value.donationImageId = value;
      }
    });
    watch(
      activeRequestSessionOrganizationGroupCauses,
      (values) => {
        if (values) {
          if (values.some((dc: DonationCause) => dc.externalOrder > 0)) {
            values.sort((dc1: DonationCause, dc2: DonationCause) =>
              dc1.externalOrder == 0
                ? 1
                : dc2.externalOrder == 0
                  ? -1
                  : dc1.externalOrder - dc2.externalOrder
            );
          }
        }
      },
      { immediate: true }
    );

    // Created
    onMounted(() => {
      if (hasAnyActiveRequestSessionOrganizationGroupCauses.value) {
        fetchImages(requestSession.value.organizationGroup.id);
      }
    });

    return {
      loading,
      showCause,
      senderName,
      hasAnyActiveRequestSessionOrganizationGroupCauses,
      donationDetails,
      greetingValue,
      showCondolenceOptions,
      noSenderName,
      selectedImageId,
      nonEmptyCategories,
      rules,
      t,
      getAttributeValueById,
      setAttributeValueById,
      fetchImages,
      selectMessage,
      validateSenderName,
      handleDonationCausesFromDonationCase,
      getDonationCauseWithId,
      getDonationCausesWithIds,
      dialog,
      defaultSelectableDonationCauses,
      activeRequestSessionOrganizationGroupCauses,
      images,
      categories,
      isIphone,
      reset,
      attributes,
      exampleMessages,
    };
  },
});
</script>
<style scoped>
.no-sender-name {
  margin: 0;
  padding: 0;
}
</style>
